<script setup lang="ts">
import { computed, ref } from 'vue'
import { useHead } from '@unhead/vue'
import { RouterView, useRoute } from 'vue-router'
import SiteHeader from './components/SiteHeader.vue'

const route = useRoute()
const publicBase = import.meta.env.BASE_URL
const siteUrl = 'https://pentestcamp.dsecure.me'
const canonicalPath = computed(() => String(route.meta.canonicalPath ?? route.path))
const canonical = computed(() => `${siteUrl}${canonicalPath.value}`)
const locale = computed(() => route.meta.locale === 'en' ? 'en' : 'pl')
const pageTitle = computed(() => String(route.meta.title ?? 'PentestCamp'))
const pageDescription = computed(() => String(route.meta.description ?? 'Praktyczne laboratoria pentestingu.'))
const alternate = computed(() => `${siteUrl}${String(route.meta.alternate ?? '/')}`)
const polishUrl = computed(() => locale.value === 'pl' ? canonical.value : alternate.value)
const englishUrl = computed(() => locale.value === 'en' ? canonical.value : alternate.value)
const homeUrl = computed(() => locale.value === 'en' ? `${publicBase}en/` : publicBase)
const aboutUrl = computed(() => locale.value === 'en' ? `${publicBase}en/about/` : `${publicBase}about/`)
const contactUrl = computed(() => locale.value === 'en' ? `${publicBase}en/contact/` : `${publicBase}contact/`)
const privacyUrl = computed(() => locale.value === 'en' ? `${publicBase}en/privacy/` : `${publicBase}privacy/`)
const imageAlt = computed(() => locale.value === 'en'
  ? 'PentestCamp, hands-on pentesting labs for education'
  : 'PentestCamp, praktyczne laboratoria pentestingu dla edukacji')
const mainElement = ref<HTMLElement | null>(null)
const skipToContent = () => mainElement.value?.focus()

const structuredData = computed(() => {
  const organization = {
    '@type': 'Organization',
    '@id': 'https://dsecure.me/#organization',
    name: 'DSecure.me',
    legalName: 'DSecure.me Sp. z o.o.',
    url: 'https://dsecure.me/',
    logo: `${siteUrl}/images/dsecure-logo-violet.png`,
    email: 'contact@dsecure.me',
    taxID: '8971889380',
    vatID: 'PL8971889380',
    identifier: [
      { '@type': 'PropertyValue', propertyID: 'KRS', value: '0000887405' },
      { '@type': 'PropertyValue', propertyID: 'REGON', value: '38836074600000' },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'ul. Na Grobli 12, bud. L3, pok. 021',
      postalCode: '50-421',
      addressLocality: 'Wrocław',
      addressCountry: 'PL',
    },
    sameAs: ['https://www.linkedin.com/company/dsecure-me/', 'https://github.com/orgs/DSecureMe/'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'contact@dsecure.me',
      availableLanguage: ['pl', 'en'],
      areaServed: ['PL', 'EU'],
    },
  }
  const website = {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: `${siteUrl}/`,
    name: 'PentestCamp',
    alternateName: 'Pentest Camp',
    inLanguage: ['pl', 'en'],
    publisher: { '@id': 'https://dsecure.me/#organization' },
  }
  const software = {
    '@type': 'SoftwareApplication',
    '@id': `${siteUrl}/#software`,
    name: 'PentestCamp',
    url: `${siteUrl}/`,
    applicationCategory: 'EducationalApplication',
    applicationSubCategory: 'Cybersecurity training platform',
    operatingSystem: 'Web',
    inLanguage: ['pl', 'en'],
    provider: { '@id': 'https://dsecure.me/#organization' },
    isPartOf: { '@id': `${siteUrl}/#website` },
    description: locale.value === 'en'
      ? 'A platform for running hands-on pentesting courses with custom scenarios, isolated lab instances, groups, schedules and progress analytics.'
      : 'Platforma do prowadzenia praktycznych laboratoriów pentestingu z własnymi scenariuszami, izolowanymi instancjami, grupami, harmonogramem i analizą postępu.',
    audience: [
      { '@type': 'EducationalAudience', educationalRole: 'student' },
      { '@type': 'EducationalAudience', educationalRole: 'teacher' },
    ],
    featureList: locale.value === 'en'
      ? ['Isolated labs per participant', 'Dynamic flags', 'Groups and schedules', 'Live lecturer dashboard', 'Participant profiles and KPIs', 'Cloud or on-premise deployment']
      : ['Izolowane laboratoria per uczestnik', 'Dynamiczne flagi', 'Grupy i harmonogram', 'Monitoring zajęć na żywo', 'Profile uczestników i KPI', 'Wdrożenie cloud lub on-premise'],
    image: `${siteUrl}/og-card.png`,
  }
  const webPage = {
    '@type': String(route.meta.schemaType ?? 'WebPage'),
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    description: pageDescription.value,
    inLanguage: locale.value,
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#software` },
  }

  const graph: object[] = [webPage]
  if (route.path === '/' || route.path === '/en/') {
    graph.unshift(organization, website, software)
  }
  else {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonical.value}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: locale.value === 'en' ? 'Home' : 'Strona główna', item: locale.value === 'en' ? `${siteUrl}/en/` : `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: pageTitle.value, item: canonical.value },
      ],
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
})

useHead({
  htmlAttrs: { lang: locale },
  title: pageTitle,
  meta: [
    { 'http-equiv': 'content-language', content: locale },
    { name: 'robots', content: computed(() => String(route.meta.robots ?? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')) },
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:url', content: canonical },
    { property: 'og:locale', content: computed(() => locale.value === 'en' ? 'en_US' : 'pl_PL') },
    { property: 'og:locale:alternate', content: computed(() => locale.value === 'en' ? 'pl_PL' : 'en_US') },
    { property: 'og:image:alt', content: imageAlt },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription },
    { name: 'twitter:image:alt', content: imageAlt },
  ],
  link: [
    { rel: 'canonical', href: canonical },
    { rel: 'alternate', hreflang: 'pl', href: polishUrl },
    { rel: 'alternate', hreflang: 'en', href: englishUrl },
    { rel: 'alternate', hreflang: 'x-default', href: polishUrl },
  ],
  script: [{
    type: 'application/ld+json',
    innerHTML: computed(() => JSON.stringify(structuredData.value)),
  }],
})
</script>

<template>
  <button class="skip-link" type="button" @click="skipToContent">{{ locale === 'en' ? 'Skip to content' : 'Przejdź do treści' }}</button>
  <div class="site-shell">
    <SiteHeader />
    <main id="main" ref="mainElement" tabindex="-1"><RouterView /></main>
    <footer class="site-footer">
      <a class="wordmark wordmark-footer" :href="homeUrl"><span class="wordmark-product">PentestCamp</span><span class="wordmark-parent">by DSecure.me</span></a>
      <p>{{ locale === 'en' ? 'A platform for hands-on application security education.' : 'Platforma do praktycznej nauki bezpieczeństwa aplikacji.' }}</p>
      <nav class="footer-nav" :aria-label="locale === 'en' ? 'Trust and legal information' : 'Informacje o firmie i prywatności'">
        <a :href="aboutUrl">{{ locale === 'en' ? 'About' : 'O nas' }}</a>
        <a :href="contactUrl">{{ locale === 'en' ? 'Contact' : 'Kontakt' }}</a>
        <a :href="privacyUrl">{{ locale === 'en' ? 'Privacy' : 'Prywatność' }}</a>
      </nav>
      <p class="footer-legal">{{ locale === 'en' ? 'A DSecure.me product' : 'Produkt DSecure.me' }} · © {{ new Date().getFullYear() }}</p>
    </footer>
  </div>
</template>
